/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import AppleIcon from "@mui/icons-material/Apple";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import ParticlesBackground from "layouts/authentication/components/ParticlesBackground";

import MDSnackbar from "components/MDSnackbar";
import { LoginCall, SocialAuth } from "../../../redux/api/auth";
import { store } from "../../../redux/store";
import { AUTH_ERROR_SESSION_KEY } from "../../../utils/logout";
import {
  getAppleWebSignInConfig,
  performAppleWebSignIn,
} from "../../../utils/appleSignInWeb";

function Basic() {
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "Wrong credential or you do not have permission to access this section. Please contact the admin."
  );
  const [appleLoading, setAppleLoading] = useState(false);
  const appleConfigured = getAppleWebSignInConfig().enabled;
  /*
    Temporarily hide the "Sign in with Apple" CTA on the web dashboard.
    Flip to `true` to bring it back without touching anything else —
    the handler, state, imports, and JSX are all left intact below.
  */
  const SHOW_APPLE_SIGN_IN = false;

  const closeErrorSB = () => setErrorSB(false);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(AUTH_ERROR_SESSION_KEY);
      if (stored) {
        sessionStorage.removeItem(AUTH_ERROR_SESSION_KEY);
        setErrorMessage(stored);
        setErrorSB(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const onClickLogin = async () => {
    const body = {
      password,
      email: email?.toLowerCase(),
    };
    try {
      const response = await LoginCall(body);

      if (
        response?.status == 400 ||
        response?.status == 403 ||
        response?.status == 404
      ) {
        setErrorMessage(
          response?.message ||
            "Wrong credential or you do not have permission to access this section. Please contact the admin."
        );
        setErrorSB(true);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.log("pippo error", error);
    }
  };

  const onChangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const onChangePassword = (e) => {
    setPassword(e.target.value);
  };

  const handleAppleSignIn = async () => {
    if (!appleConfigured) {
      setErrorMessage(
        "Sign in with Apple is not enabled in this build. Add REACT_APP_APPLE_CLIENT_ID (and matching APPLE_WEB_CLIENT_ID on the API) to your environment, then rebuild."
      );
      setErrorSB(true);
      return;
    }
    setAppleLoading(true);
    try {
      const { idToken, sub, email: appleEmail } = await performAppleWebSignIn();
      const response = await SocialAuth({
        email: (appleEmail || "").toLowerCase().trim(),
        social_auth_provider: "apple",
        social_auth_id: sub,
        apple_identity_token: idToken,
      });
      if (store.getState().auth?.access_token) {
        window.location.reload();
        return;
      }
      if (response?.access_token) {
        setErrorMessage(
          "Your account is not allowed on this dashboard, or the session could not be started. Please contact an admin."
        );
        setErrorSB(true);
        return;
      }
      if (response?.status === 404) {
        setErrorMessage(
          "No Microlab account was found for this Apple ID. Use the email and password for your dashboard account, or contact an admin."
        );
      } else if (response?.status === 503) {
        setErrorMessage(
          "Sign in with Apple is not enabled on the server yet. Please use email and password, or try again later."
        );
      } else {
        setErrorMessage(
          response?.message || "Apple sign-in failed. Please try again or use email and password."
        );
      }
      setErrorSB(true);
    } catch (err) {
      const code = err?.error;
      if (code === "popup_closed_by_user" || err?.message?.includes("cancel")) {
        return;
      }
      setErrorMessage(err?.message || "Apple sign-in was cancelled or failed.");
      setErrorSB(true);
    } finally {
      setAppleLoading(false);
    }
  };

  return (
    <BasicLayout animation={<ParticlesBackground />}>
      <Card>
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translate(-50%, -60%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={require("../../../assets/images/logos/mini_logo.png")}
            style={{ height: 70, width: 70, borderRadius: 10 }}
            alt=""
          />
        </div>
        <MDBox mb={2} />
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>Log Into Microlab</MDBox>
            <MDBox mb={2}>
              <MDInput
                onChange={onChangeEmail}
                type="email"
                label="Email"
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                onChange={onChangePassword}
                type="password"
                label="Password"
                fullWidth
              />
            </MDBox>
            <MDBox mb={1} textAlign="right">
              <MDTypography
                component={Link}
                to="/authentication/forgot-password"
                variant="button"
                color="info"
                fontWeight="medium"
                sx={{ textDecoration: "none" }}
              >
                Forgot password?
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton
                onClick={onClickLogin}
                variant="gradient"
                color="info"
                fullWidth
              >
                sign in
              </MDButton>
            </MDBox>
            {SHOW_APPLE_SIGN_IN && (
              <>
                <MDBox mt={2} mb={0.5}>
                  <MDButton
                    type="button"
                    variant="contained"
                    color="dark"
                    fullWidth
                    disabled={appleLoading || !appleConfigured}
                    onClick={handleAppleSignIn}
                    startIcon={<AppleIcon sx={{ fontSize: 22 }} />}
                    sx={{
                      py: 1.1,
                      fontWeight: 600,
                      bgcolor: appleConfigured ? "#000" : "grey.600",
                      color: "#fff",
                      "&:hover": { bgcolor: appleConfigured ? "#1c1c1e" : "grey.700" },
                    }}
                  >
                    {appleLoading ? "Connecting…" : "Sign in with Apple"}
                  </MDButton>
                </MDBox>
                {!appleConfigured ? (
                  <MDBox mb={1} textAlign="center">
                    <MDTypography variant="caption" color="text">
                      Apple sign-in requires{" "}
                      <code style={{ fontSize: "0.75rem" }}>REACT_APP_APPLE_CLIENT_ID</code> in{" "}
                      <code style={{ fontSize: "0.75rem" }}>.env</code>.
                    </MDTypography>
                  </MDBox>
                ) : null}
              </>
            )}
            <MDSnackbar
              color="error"
              icon="warning"
              title={errorMessage}
              dateTime=""
              content=""
              open={errorSB}
              onClose={closeErrorSB}
              close={closeErrorSB}
              bgWhite
            />
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
